import { useState } from "react";
import { Plug, Plus, Trash2 } from "lucide-react";
import { Button, Dialog, Input, Select } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { ConnectorDialog } from "./ConnectorDialog";

const KINDS = [
  { value: "datanorm", label: "nur Katalog (Datanorm)" },
  { value: "ids_connect", label: "IDS Connect (+ Katalog)" },
];

export function SuppliersDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const list = trpc.supplier.list.useQuery(undefined, { enabled: open });
  const [name, setName] = useState("");
  const [kind, setKind] = useState("datanorm");
  const [connectorFor, setConnectorFor] = useState<string | null>(null);

  const refresh = () => {
    utils.supplier.list.invalidate();
    utils.article.list.invalidate();
  };
  const create = trpc.supplier.create.useMutation({
    onSuccess: () => {
      setName("");
      refresh();
    },
    onError: (e) =>
      addToast({ type: "error", title: "Fehler", description: e.message }),
  });
  const remove = trpc.supplier.remove.useMutation({ onSuccess: refresh });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Lieferanten" wide>
      <div className="space-y-2 mb-4">
        {(list.data ?? []).map((s) => (
          <div
            key={s.id}
            className="flex items-center justify-between card p-2.5 text-sm"
          >
            <span>
              <span className="text-text-primary font-medium">{s.name}</span>
              <span className="text-text-tertiary text-xs ml-2">
                {[
                  s._count.prices
                    ? `Katalog: ${s._count.prices.toLocaleString("de-AT")} Preise`
                    : null,
                  s.connectorKind === "ids_connect" ? "IDS Connect" : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "kein Katalog, kein Konnektor"}
              </span>
            </span>
            <span className="flex items-center gap-1">
              <button
                className="text-text-tertiary hover:text-accent"
                title="Konnektor konfigurieren"
                onClick={() => setConnectorFor(s.id)}
              >
                <Plug className="w-3.5 h-3.5" />
              </button>
              <button
                className="text-text-tertiary hover:text-danger"
                onClick={() => remove.mutate({ id: s.id })}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </span>
          </div>
        ))}
        {!list.data?.length && (
          <p className="text-sm text-text-tertiary">Noch keine Lieferanten.</p>
        )}
      </div>
      <div className="flex items-end gap-2 border-t border-border-subtle pt-3">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z. B. Holter, ÖAG, GC Österreich"
        />
        <Select
          label="Konnektor"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          options={KINDS}
        />
        <Button
          variant="secondary"
          loading={create.isPending}
          disabled={!name.trim()}
          onClick={() =>
            create.mutate({ name: name.trim(), connectorKind: kind as never })
          }
        >
          <Plus className="w-4 h-4" /> Anlegen
        </Button>
      </div>

      <ConnectorDialog
        supplierId={connectorFor}
        open={!!connectorFor}
        onClose={() => setConnectorFor(null)}
      />
    </Dialog>
  );
}
